import React from 'react';
import { Input } from '../UI';
import { fmt } from '../../utils';

const NAVY = '#0b318b';
const CTRL = { fontSize: 11, padding: '6px 9px', borderRadius: 7, minHeight: 32, boxSizing: 'border-box' };
const CELL = { border: '1px solid #cbd5e1', padding: '5px 8px', fontSize: 11 };
const HEAD = { ...CELL, background: '#243b64', color: '#fff', fontWeight: 700, fontSize: 10, letterSpacing: 0.3, textTransform: 'uppercase' };

const METHOD_LABEL = {
  full_next_payment: 'Full on next payment',
  pro_rata: 'Pro-rata',
  manual: 'Manual',
};

/**
 * Offsetting earlier advances against this voucher.
 *
 * The supplier already holds the advanced money, so recovering it here does
 * two things at once: it reduces what they are paid now, and it releases the
 * requisition headroom the advance was holding. Without it you double-count —
 * advance N30,000, pay N100,000 for the same goods, and the requisition
 * reports N130,000 consumed against N100,000 of value.
 *
 * Amounts are pre-filled from each advance's own recovery method but stay
 * editable: the person raising the payment is the one who knows whether the
 * full advance is genuinely being worked off on this delivery.
 */
export default function AdvanceRecoveryPanel({ advances, amounts, setAmount, totalRecovered, amountPayable }) {
  const overPayable = totalRecovered > amountPayable + 0.01;

  return (
    <div style={{ border: '1px solid #fbbf24', background: '#fffdf5', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', letterSpacing: 0.4 }}>ADVANCE RECOVERY</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          This requisition has {advances.length} outstanding advance{advances.length === 1 ? '' : 's'}. Recovering here reduces what the supplier is paid now.
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead><tr>
            {['Advance Voucher', 'Date', 'Advance Amount', 'Already Recovered', 'Outstanding', 'Recovery Method', 'Recover Now']
              .map(h => <th key={h} style={{ ...HEAD, textAlign: 'center' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {advances.map((a) => {
              const val = amounts[a.advance_voucher_id] ?? '';
              const over = (Number(val) || 0) > Number(a.outstanding_amount) + 0.01;
              return (
                <tr key={a.advance_voucher_id}>
                  <td style={{ ...CELL, fontFamily: 'var(--mono)', color: NAVY, fontWeight: 700 }}>{a.voucher_no || '—'}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{a.voucher_date || '—'}</td>
                  <td style={{ ...CELL, textAlign: 'right' }}>{fmt(a.advance_amount)}</td>
                  <td style={{ ...CELL, textAlign: 'right', color: '#2563eb' }}>{fmt(a.recovered_amount)}</td>
                  <td style={{ ...CELL, textAlign: 'right', fontWeight: 700, color: '#c2410c' }}>{fmt(a.outstanding_amount)}</td>
                  <td style={{ ...CELL, textAlign: 'center' }}>{METHOD_LABEL[a.recovery_method] || '—'}</td>
                  <td style={{ ...CELL, padding: 2 }}>
                    <Input
                      type="number" value={val}
                      onChange={v => setAmount(a.advance_voucher_id, v)}
                      style={{ ...CTRL, textAlign: 'right', ...(over ? { border: '1px solid var(--red)' } : {}) }}
                      title={over ? 'More than this advance still has outstanding' : undefined}
                    />
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={6} style={{ ...CELL, textAlign: 'right', fontWeight: 800, background: '#f1f5f9' }}>TOTAL RECOVERED ON THIS VOUCHER:</td>
              <td style={{ ...CELL, textAlign: 'right', fontWeight: 800, background: '#f1f5f9', color: overPayable ? 'var(--red)' : NAVY }}>
                {fmt(totalRecovered)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
        Recovery cannot exceed what this voucher is paying — anything left over is recovered on a later payment.
      </div>
    </div>
  );
}
